import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { Settings } from '@/interfaces/Settings'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { formatDate } from '@/lib/formats/formatDate'
import { cn } from '@/lib/utils'
import { components } from '@invo.space/shared'
import { Currency } from '@/types/Currency'

import { TaxSelect } from '../components/TaxSelect'
import { ProductPriceFormSchema, ProductPriceFormValues } from './schema'

export interface ProductPriceFormProps {
  skuId: string
  onSuccess?: () => void
  variant?: 'default' | 'inline'
  successMessage?: string
}

export const ProductPriceForm = ({
  skuId,
  onSuccess,
  variant = 'default',
  successMessage,
}: ProductPriceFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.products',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const [datePickerOpen, setDatePickerOpen] = useState(false)

  const { data: settings, isLoading: isSettingsLoading } = useSWR<Settings>(
    '/api/v2/settings',
    apiFetcher,
  )

  const { data: currencies, isLoading: isCurrenciesLoading } = useSWR<
    Collection<Currency>
  >('/api/v2/currencies', apiFetcher)

  const { data: taxRates } = useSWR<Collection<components['schemas']['TaxRate']>>(
    '/api/v2/tax-rates',
    apiFetcher,
  )

  const methods = useForm<ProductPriceFormValues>({
    resolver: zodResolver(ProductPriceFormSchema),
    defaultValues: {
      price: undefined,
      taxRate: undefined,
      validFrom: undefined,
      note: undefined,
    },
  })

  const { control, watch, setValue } = methods
  const validFrom = watch('validFrom')
  const isVatPayer = settings?.vatPayer === 'vatPayer'
  const primaryCurrency = settings?.primaryCurrency || 'CZK'
  const currencySymbol =
    currencies?.data.find((c) => c.code === primaryCurrency)?.symbol ||
    primaryCurrency

  const onValid = async (values: ProductPriceFormValues) => {
    try {
      const taxRateId = values.taxRate
        ? taxRates?.data.find((tax) => tax.percentage === values.taxRate)?.id
        : undefined

      const payload: components['schemas']['SkuPriceCreate'] = {
        price: {
          amount: Math.round(values.price * 100).toString(),
          currency: primaryCurrency,
        },
        taxRate: taxRateId,
        validFrom: values.validFrom || null,
        note: values.note || null,
      }

      await API.post(
        `/api/v2/skus/${skuId}/prices`,
        payload,
      )
      toast.success(successMessage ?? t('price.add.success'))
      methods.reset()
      onSuccess?.()
    } catch (error: unknown) {
      console.error('Product price form error:', error)
      const errorMessage = extractErrorMessage(error)
      toast.error(t('price.add.error'), {
        description: errorMessage || t('error.description'),
      })
    }
  }

  const onInvalid = (errors: FieldErrors<ProductPriceFormValues>) => {
    console.log('Form errors', errors, methods.getValues())
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // Store as ISO string for API submission
      setValue('validFrom', date.toISOString())
    } else {
      setValue('validFrom', null)
    }
    setDatePickerOpen(false)
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (isSettingsLoading || isCurrenciesLoading) {
    if (variant === 'inline') {
      return (
        <div className="rounded-md bg-muted p-4">
          <Loading />
        </div>
      )
    }

    return null
  }

  if (variant === 'inline') {
    return (
      <FormProvider {...methods}>
        <div className="rounded-md bg-muted p-4">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 sm:max-w-md">
              <FormField
                control={control}
                name="validFrom"
                render={() => (
                  <FormItem>
                    <FormLabel>{t('price.form.validFrom.label')}</FormLabel>
                    <FormControl>
                      <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              'w-full justify-start text-left font-normal',
                              !validFrom && 'text-muted-foreground',
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {validFrom ? formatDate(new Date(validFrom)) : tg('pickDate')}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={validFrom ? new Date(validFrom) : undefined}
                            onSelect={handleDateSelect}
                            disabled={(date) => {
                              const dateOnly = new Date(date)
                              dateOnly.setHours(0, 0, 0, 0)
                              return dateOnly < today
                            }}
                            autoFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2 sm:max-w-md">
              <FormField
                control={control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('price.form.price.label')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          placeholder={t('price.form.price.placeholder')}
                          className={cn('pr-8', {
                            'border-destructive': !!methods.formState.errors.price,
                          })}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value
                            const numericValue = parseFloat(value)
                            if (!isNaN(numericValue)) {
                              field.onChange(numericValue)
                            } else if (value === '') {
                              field.onChange(undefined)
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              methods.handleSubmit(onValid, onInvalid)()
                            }
                          }}
                          value={field.value ?? ''}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {currencySymbol}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {isVatPayer && (
                <FormField
                  control={control}
                  name="taxRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('price.form.taxRate.label')}</FormLabel>
                      <FormControl>
                        <TaxSelect
                          control={control}
                          name="taxRate"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('price.form.taxRate.placeholder')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
              <FormField
                control={control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('price.form.note.label')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('price.form.note.placeholder')}
                        {...field}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            methods.handleSubmit(onValid, onInvalid)()
                          }
                        }}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={methods.handleSubmit(onValid, onInvalid)}
              >
                                {t('price.form.submit')}
              </Button>
            </div>
          </div>
        </div>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onValid, onInvalid)}
        className="w-full space-y-4"
      >
        <div className="grid gap-4">
          <FormField
            control={control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('price.form.price.label')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="0.00"
                      className={cn({
                        'border-destructive': !!methods.formState.errors.price,
                      })}
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value
                        const numericValue = parseFloat(value)
                        if (!isNaN(numericValue)) {
                          field.onChange(numericValue)
                        } else if (value === '') {
                          field.onChange(undefined)
                        }
                      }}
                      value={field.value ?? ''}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {currencySymbol}
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isVatPayer && (
            <FormField
              control={control}
              name="taxRate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('price.form.taxRate.label')}</FormLabel>
                  <FormControl>
                    <TaxSelect
                      control={control}
                      name="taxRate"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('price.form.taxRate.placeholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="validFrom"
            render={() => (
              <FormItem>
                <FormLabel>{t('price.form.validFrom.label')}</FormLabel>
                <FormControl>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !validFrom && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {validFrom ? formatDate(new Date(validFrom)) : tg('pickDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          validFrom ? new Date(validFrom) : undefined
                        }
                        onSelect={handleDateSelect}
                        disabled={(date) => {
                          const dateOnly = new Date(date)
                          dateOnly.setHours(0, 0, 0, 0)
                          return dateOnly < today
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('price.form.note.label')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('price.form.note.placeholder')}
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full">
                    {t('price.form.submit')}
        </Button>
      </form>
    </FormProvider>
  )
}

